import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { Button, Alert, Box } from '@mui/material';
import { sendEmailVerification } from 'firebase/auth';
import { auth } from '../../firebase/config';

export const VerifiedRoute = ({ children }) => {
    const { currentUser, checkEmailVerification } = useAuth();
    const { showNotification } = useNotification();
    const location = useLocation();
    const navigate = useNavigate();
    const [resendLoading, setResendLoading] = useState(false);
    const [checkingVerification, setCheckingVerification] = useState(false);

    // 認証状態をFirebaseから再確認する関数（AuthContextの関数を使用）
    const checkVerificationStatus = async () => {
        if (!currentUser || checkingVerification) return false;

        setCheckingVerification(true);
        try {
            const isVerified = await checkEmailVerification();

            if (isVerified && !currentUser.emailVerified) {
                showNotification('メールアドレスの認証が確認されました', 'success');
            }

            setCheckingVerification(false);
            return isVerified;
        } catch (error) {
            console.error('認証状態確認エラー:', error);
            setCheckingVerification(false);
            return false;
        }
    };

    const handleResendEmail = async () => {
        if (!currentUser || resendLoading) return;

        setResendLoading(true);
        try {
            const firebaseUser = auth.currentUser;
            if (!firebaseUser) {
                throw new Error('Firebase user not found');
            }
            await sendEmailVerification(firebaseUser, {
                url: window.location.origin + '/email-verification?verified=true'
            });
            showNotification('認証メールを再送信しました。メールをご確認ください。', 'success');
            navigate('/email-verification', { state: { from: location.pathname }, replace: true });
        } catch (error) {
            console.error('Email verification error:', error);
            let errorMessage = '認証メールの送信に失敗しました';
            if (error.code === 'auth/too-many-requests') {
                errorMessage = '送信回数が制限を超えました。しばらく時間をおいて再度お試しください。';
            } else if (error.code === 'auth/requires-recent-login') {
                errorMessage = '再度ログインが必要です。一度ログアウトして、再度ログインしてください。';
            }
            showNotification(errorMessage, 'error');
        } finally {
            setResendLoading(false);
        }
    };

    useEffect(() => {
        if (!currentUser) {
            showNotification('このページにアクセスするにはログインが必要です', 'warning');
            navigate('/login', { state: { from: location.pathname }, replace: true });
            return;
        }

        if (!currentUser.emailVerified) {
            // 認証状態を自動的に再確認（AuthContextの関数を使用）
            checkVerificationStatus().then(isVerified => {
                if (isVerified) {
                    // リロードして最新のユーザー情報を取得
                    window.location.reload();
                    return;
                }

                // 認証されていない場合はメッセージと再送信ボタンを表示
                showNotification(
                    'このページにアクセスするにはメール認証が必要です',
                    'warning',
                    null,
                    <Button
                        color="inherit"
                        size="small"
                        onClick={handleResendEmail}
                        disabled={resendLoading}
                    >
                        {resendLoading ? '送信中...' : '認証メールを再送信'}
                    </Button>
                );
            });
        } else if (currentUser.emailVerified && !currentUser.isProfileSetupCompleted && !location.pathname.includes('/profile-setup')) {
            // メール認証は完了しているが、プロフィール設定が未完了の場合
            showNotification('プロフィールの初期設定を完了してください', 'info');
            navigate('/profile-setup', { state: { from: location.pathname }, replace: true });
        }
    }, [currentUser, location.pathname, navigate, showNotification, resendLoading]);

    if (!currentUser) {
        return (
            <Box sx={{
                height: 'calc(100vh - 120px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2
            }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    このページにアクセスするにはログインが必要です
                </Alert>
            </Box>
        );
    }

    if (!currentUser.emailVerified) {
        return (
            <Box sx={{
                height: 'calc(100vh - 120px)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2
            }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    このページにアクセスするにはメール認証が必要です
                </Alert>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleResendEmail}
                        disabled={resendLoading}
                    >
                        {resendLoading ? '送信中...' : '認証メールを再送信'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            checkVerificationStatus().then(isVerified => {
                                if (isVerified) {
                                    window.location.reload();
                                } else {
                                    showNotification('まだメール認証が完了していません。メール内のリンクをクリックしてください。', 'warning');
                                }
                            });
                        }}
                        disabled={checkingVerification}
                    >
                        {checkingVerification ? '確認中...' : '認証状態を確認'}
                    </Button>
                </Box>
            </Box>
        );
    }

    // プロフィール設定が完了していない場合のみ、profile-setupページにリダイレクト
    if (currentUser.emailVerified && !currentUser.isProfileSetupCompleted && !location.pathname.includes('/profile-setup')) {
        return <Navigate
            to="/profile-setup"
            state={{ from: location.pathname }}
            replace
        />;
    }

    return children;
}; 